import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Button,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { pictoWebVr, useViewport } from "../../helpers";
import { QRCode } from "react-qrcode-logo";
import { myDesignService } from "../../services";
import clsx from "clsx";
import SettingsIcon from "@mui/icons-material/Settings";
import { useSnackbar } from "notistack";
import ViewInAR from "../../assets/viewInAR.svg";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ShareIcon from "@mui/icons-material/Share";
import LinkIcon from "@mui/icons-material/Link";
import FanArtZone from "../../assets/faz.png";
import { useNavigate } from "react-router-dom/dist";
import { objectSizeCameraPos } from "../../helpers/model-viewer-camera-position";

const PREFIX = "WebVR";

const classes = {
  root: `${PREFIX}-root`,
  vrContainer: `${PREFIX}-vrContainer`,
  optionsContainer: `${PREFIX}-optionsContainer`,
  selectSizeContainer: `${PREFIX}-selectSizeContainer`,
  selectMoldContainer: `${PREFIX}-selectMoldContainer`,
  qrPosition: `${PREFIX}-qrPosition`,
  downloadQRButton: `${PREFIX}-downloadQRButton`,
  actionButton: `${PREFIX}-actionButton`,
  ADSIconButton: `${PREFIX}-ADSIconButton`,
  updateContainer: `${PREFIX}-updateContainer`,
  savingInfos: `${PREFIX}-savingInfos`,
  loaderContainer: `${PREFIX}-loaderContainer`,
  webVrPoster: `${PREFIX}-webVrPoster`,
  webVrButton: `${PREFIX}-webVrButton`,
  activateARText: `${PREFIX}-activateARText`,
  buttonsQrCode: `${PREFIX}-buttonsQrCode`,
  shareIconButton: `${PREFIX}-shareIconButton`,
  copyQrUrlButton: `${PREFIX}-copyQrUrlButton`,
  selectMenuStyle: `${PREFIX}-selectMenuStyle`,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`& .${classes.root}`]: {
    overflow: "hidden",
    display: "flex",
    [theme.breakpoints.between("xs", "md")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.vrContainer}`]: {
    width: "50%",
    [theme.breakpoints.between("xs", "md")]: {
      width: "100%",
    },
  },

  [`& .${classes.optionsContainer}`]: {
    width: "50%",
    padding: " 0 2rem",
    [theme.breakpoints.between("xs", "md")]: {
      width: "100%",
      padding: "inherit",
      margin: "1rem 0",
    },
  },

  [`& .${classes.selectSizeContainer}`]: {
    margin: "1rem 0 1rem 0",
    [theme.breakpoints.between("xs", "md")]: {
      margin: "0 0 1rem 0",
    },
    width: "100%",
  },

  [`& .${classes.selectMoldContainer}`]: {
    margin: "0 0 1rem 0",
    width: "100%",
  },

  [`& .${classes.qrPosition}`]: {
    display: "flex",
    justifyContent: "center",
    marginTop: "2rem",
  },

  [`& .${classes.downloadQRButton}`]: {
    margin: "1rem 0",
  },

  [`& .${classes.actionButton}`]: {
    backgroundColor: theme.palette.primary.main,
    width: "40px",
    height: "40px",
  },

  [`& .${classes.ADSIconButton}`]: {
    height: "50%",
    color: "rgba(255,255,255,1)",
  },

  [`& .${classes.updateContainer}`]: {
    display: "flex",
    justifyContent: "space-between",
  },

  [`& .${classes.savingInfos}`]: {
    width: "50%",
    position: "relative",
    [theme.breakpoints.between("xs", "md")]: {
      width: "100%",
    },
  },

  [`& .${classes.loaderContainer}`]: {
    position: "absolute",
    left: "50%",
    top: "40%",
    transform: "translate(-40%, -50%)",
    textAlign: "center",
    [theme.breakpoints.between("xs", "md")]: {
      position: "inherit",
      left: "inherit",
      top: "inherit",
      transform: "inherit",
      textAlign: "center",
      marginTop: "1rem",
    },
  },

  [`& .${classes.webVrPoster}`]: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundImage: `url(${FanArtZone})`,
    backgroundSize: "250px 250px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
  },

  [`& .${classes.webVrButton}`]: {
    backgroundColor: theme.palette.primary.main,
    color: "#434343",
    borderRadius: "4px",
    border: "none",
    position: "absolute",
    top: "1.5rem",
    right: "16px",
    zIndex: "999",
    display: "flex",
  },

  [`& .${classes.activateARText}`]: {
    transform: "translateY(5px)",
    marginLeft: "0.5rem",
  },

  [`& .${classes.buttonsQrCode}`]: {
    display: "flex",
    justifyContent: "space-around",
  },

  [`& .${classes.shareIconButton}`]: {
    color: "#343434",
    backgroundColor: theme.palette.primary.main,
    width: "40px",
    height: "40px",
    margin: "1rem 0",
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: "#343434",
    },
  },

  [`& .${classes.copyQrUrlButton}`]: {
    display: "flex",
    justifyContent: "center",
  },

  [`& .${classes.selectMenuStyle}`]: {
    backgroundColor: "#424242",
  },
}));

const WebVR = (props) => {
  const { t } = useTranslation();
  const { width } = useViewport();
  const history = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const canvasRef = useRef(null);
  const [picto, setPicto] = useState(null);
  const [state, setState] = useState({
    size: "medium",
    molding: "design",
    saving: false,
    qrUrl: null,
  });
  const [copied, setCopied] = useState(false);
  const [dimensions, setDimensions] = useState({});
  const [isLandscape, setIsLandscape] = useState(false);

  const shareMedia = () => {
    navigator.share({
      title: document.title,
      text: `${t("myDesign.webVR.shareExparienceStart")}${
        props.selectedTarget.label
      }${t("myDesign.webVR.shareExparienceEnd")}`,
      url: state.qrUrl,
    });
  };

  useEffect(() => {
    setPicto(pictoWebVr);
  }, []);

  React.useEffect(() => {
    if (props.selectedTarget) {
      var w;
      var h;
      var img = new Image();
      img.src = `${process.env.REACT_APP_API_URL}/targets/${props.selectedTarget.id}/image?${props.borderlessUrl}`;
      img.onload = function () {
        w = this.width;
        h = this.height;
        setDimensions({ x: w, y: h, z: 1 });
      };

      setState({
        size: props.selectedTarget.size || "medium",
        molding: props.selectedTarget.molding || "design",
        saving: false,
        qrUrl: props.selectedTarget.shortLink,
      });
    }
  }, [props.selectedTarget]);

  React.useEffect(() => {
    if (
      props.frameId &&
      props.selectedTarget &&
      Object.keys(dimensions).length > 0
    ) {
      const tmp = document.getElementById("model-viewer");
      tmp.addEventListener("ar-status", (event) => {
        if (event.detail.status === "failed") {
          // if device is not compatible AR
          const error = document.getElementById("error");
          error.classList.remove("hide");
          error.addEventListener("transitionend", (event) => {
            error.classList.add("hide");
          });
        }
      });
      tmp.addEventListener("load", () => {
        let painting = tmp.model.materials.filter((material) =>
          material.name.includes("content")
        )[0];
        let createAndApplyTexture = async (channel) => {
          const texture = await tmp.createTexture(
            `${process.env.REACT_APP_API_URL}/targets/${props.selectedTarget.id}/image?${props.borderlessUrl}`
          );
          painting.pbrMetallicRoughness[channel].setTexture(texture);
        };
        createAndApplyTexture("baseColorTexture");
        let x = 1,
          y = 1,
          z = 1;
        if (dimensions.x === dimensions.y) {
          setIsLandscape(false);
          x = 1.34;
          y = 1;
          z = 1;
        } else if (dimensions.x > dimensions.y) {
          setIsLandscape(true);
          const ratio = dimensions.x / dimensions.y;
          x = ratio;
          y = 1.34;
          z = 1;
        } else {
          setIsLandscape(false);
          const ratio = dimensions.y / dimensions.x;
          x = 1;
          y = ratio / 1.34;
          z = 1;
        }
        tmp.scale = `${x} ${y} ${z}`;
      });

      //panning (relocate camera by touching/clicking)
      const tapDistance = 2;
      let panning = false;
      let panX, panY;
      let startX, startY;
      let lastX, lastY;
      let metersPerPixel;

      const startPan = () => {
        const orbit = tmp.getCameraOrbit();
        const { theta, phi, radius } = orbit;
        const psi = theta - tmp.turntableRotation;
        metersPerPixel = (0.75 * radius) / tmp.getBoundingClientRect().height;
        panX = [-Math.cos(psi), 0, Math.sin(psi)];
        panY = [
          -Math.cos(phi) * Math.sin(psi),
          Math.sin(phi),
          -Math.cos(phi) * Math.cos(psi),
        ];
        tmp.interactionPrompt = "none";
      };

      const movePan = (thisX, thisY) => {
        const dx = (thisX - lastX) * metersPerPixel;
        const dy = (thisY - lastY) * metersPerPixel;
        lastX = thisX;
        lastY = thisY;

        const target = tmp.getCameraTarget();
        target.x += dx * panX[0] + dy * panY[0];
        target.y += dx * panX[1] + dy * panY[1];
        target.z += dx * panX[2] + dy * panY[2];
        tmp.cameraTarget = `${target.x}m ${target.y}m ${target.z}m`;

        // This pauses turntable rotation
        tmp.dispatchEvent(
          new CustomEvent("camera-change", {
            detail: { source: "user-interaction" },
          })
        );
      };

      const recenter = (pointer) => {
        panning = false;
        if (
          Math.abs(pointer.clientX - startX) > tapDistance ||
          Math.abs(pointer.clientY - startY) > tapDistance
        )
          return;
        const hit = tmp.positionAndNormalFromPoint(
          pointer.clientX,
          pointer.clientY
        );
        tmp.cameraTarget =
          hit == null ? "auto auto auto" : hit.position.toString();
      };

      tmp.addEventListener(
        "mousedown",
        (event) => {
          startX = event.clientX;
          startY = event.clientY;
          panning =
            event.button === 2 ||
            event.ctrlKey ||
            event.metaKey ||
            event.shiftKey;
          if (!panning) return;

          lastX = startX;
          lastY = startY;
          startPan();
          event.stopPropagation();
        },
        true
      );

      tmp.addEventListener(
        "touchstart",
        (event) => {
          const { targetTouches, touches } = event;
          startX = targetTouches[0].clientX;
          startY = targetTouches[0].clientY;
          panning =
            targetTouches.length === 2 &&
            targetTouches.length === touches.length;
          if (!panning) return;

          lastX = 0.5 * (targetTouches[0].clientX + targetTouches[1].clientX);
          lastY = 0.5 * (targetTouches[0].clientY + targetTouches[1].clientY);
          startPan();
        },
        true
      );

      window.addEventListener(
        "mousemove",
        (event) => {
          if (!panning) return;

          movePan(event.clientX, event.clientY);
          event.stopPropagation();
        },
        true
      );

      tmp.addEventListener(
        "touchmove",
        (event) => {
          if (!panning || event.targetTouches.length !== 2) return;

          const { targetTouches } = event;
          const thisX =
            0.5 * (targetTouches[0].clientX + targetTouches[1].clientX);
          const thisY =
            0.5 * (targetTouches[0].clientY + targetTouches[1].clientY);
          movePan(thisX, thisY);
        },
        true
      );

      window.addEventListener(
        "mouseup",
        (event) => {
          recenter(event);
        },
        true
      );
    }
  }, [props.frameId, props.borderlessUrl, dimensions]);

  const downloadQR = () => {
    let canvas = canvasRef.current.querySelector("canvas");
    let image = canvas.toDataURL("image/jpg");
    let anchor = document.createElement("a");
    anchor.href = image;
    anchor.download = `QRCode-${props.selectedTarget.label.replace(
      /\s/g,
      ""
    )}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const updateExparience = () => {
    if (props.selectedTarget.idExparience) setState({ ...state, saving: true });
    myDesignService
      .updateExparienceFromMyDesign(
        props.frameId,
        props.selectedTarget.id,
        state.molding,
        state.size
      )
      .then((res) => {
        props.setSelectedTarget(res);
        if (props.selectedTarget.idExparience)
          setState({ ...state, saving: false });
        enqueueSnackbar(t("toasts.updateExparienceFromMyDesignSuccess"), {
          variant: "success",
        });
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.updateExparienceFromMyDesignError"), {
            variant: "error",
          });
      });
  };

  if (
    !props.currentDesign ||
    !props.selectedTarget ||
    Object.keys(dimensions).length <= 0
  ) {
    return (
      <div className="loading-container">
        <CircularProgress size={50} />
        <Typography>
          {state.saving ? t("loaders.saving") : t("loaders.fetching")}
        </Typography>
      </div>
    );
  } else {
    return (
      <StyledContainer>
        <div
          style={{
            maxWidth: width > 1024 ? "80%" : "inherit",
            margin: "0 auto",
            marginBottom: width < 1024 ? "5rem" : "1rem",
          }}
        >
          <div className={classes.root}>
            <div className={classes.vrContainer}>
              <model-viewer
                src={`https://ar-prod.s3.fr-par.scw.cloud/0_FRAMES_AR/${
                  state.molding
                }${isLandscape ? "-landscape" : ""}-${state.size}.glb`}
                skybox-image="https://ar-poc.s3.fr-par.scw.cloud/test/360.hdr"
                alt="3D mydesign in VR"
                ar
                shadow-intensity="1"
                ar-modes="webxr scene-viewer quick-look"
                camera-controls
                ar-placement="wall"
                id="model-viewer"
                min-field-of-view="70deg"
                max-field-of-view="85deg"
                min-camera-orbit="-Infinity 22.5deg 0.5m"
                max-camera-orbit="Infinity 157.5deg 1.5m"
                camera-orbit={`0deg 90deg ${
                  objectSizeCameraPos[state.size][
                    isLandscape ? "landscape" : "portrait"
                  ]
                }`} // initial angle and position of camera
                style={{
                  width: "100%",
                  height: width > 960 ? "600px" : "400px",
                }}
              >
                <div
                  id="lazy-load-poster"
                  slot="poster"
                  className={classes.webVrPoster}
                ></div>
                <button
                  slot="ar-button"
                  className={clsx(classes.webVrButton, "animated-button")}
                >
                  <img id="ViewInAR" alt="ViewInAR-icon" src={ViewInAR} />
                  <div className={classes.activateARText}>
                    {t("myDesign.webVR.activateAR")}
                  </div>
                </button>
                <div id="error" className="hide">
                  {t("myDesign.webVR.ARNotSupported")}
                </div>
              </model-viewer>
            </div>
            {state.saving ? (
              <div className={classes.savingInfos}>
                <div className={classes.loaderContainer}>
                  <CircularProgress size={50} />
                  <Typography>{t("loaders.updating")}</Typography>
                </div>
              </div>
            ) : (
              <div className={classes.optionsContainer}>
                <FormControl
                  variant="outlined"
                  className={clsx(
                    classes.selectSizeContainer,
                    classes.flexContainer
                  )}
                >
                  <InputLabel id="size-simple-select-outlined-label">
                    {t("myExpariences.form.size")}
                  </InputLabel>
                  <Select
                    labelId="size-simple-select-outlined-label"
                    id="size-simple-select-outlined"
                    value={state.size}
                    MenuProps={{ classes: { paper: classes.selectMenuStyle } }}
                    onChange={(e) => {
                      myDesignService
                        .updateSize(props.selectedTarget.id, e.target.value)
                        .then(() => {
                          setState({ ...state, size: e.target.value });
                          props.setSelectedTarget({
                            ...props.selectedTarget,
                            size: e.target.value,
                          });
                        })
                        .catch((err) => {
                          if (err === 403)
                            enqueueSnackbar(t("toasts.unauthorizedError"), {
                              variant: "error",
                            });
                          else
                            enqueueSnackbar(t("toasts.updateMyDesignSize"), {
                              variant: "error",
                            });
                        });
                    }}
                    label={t("myExpariences.form.size")}
                  >
                    <MenuItem value="small">
                      {t("myExpariences.form.small")}
                    </MenuItem>
                    <MenuItem value="medium">
                      {t("myExpariences.form.medium")}
                    </MenuItem>
                    <MenuItem value="large">
                      {t("myExpariences.form.large")}
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl
                  variant="outlined"
                  className={classes.selectMoldContainer}
                >
                  <InputLabel id="molding-simple-select-outlined-label">
                    {t("myExpariences.form.molding")}
                  </InputLabel>
                  <Select
                    labelId="molding-simple-select-outlined-label"
                    id="molding-simple-select-outlined"
                    MenuProps={{ classes: { paper: classes.selectMenuStyle } }}
                    value={state.molding}
                    onChange={(e) => {
                      myDesignService
                        .updateMolding(props.selectedTarget.id, e.target.value)
                        .then(() => {
                          setState({ ...state, molding: e.target.value });
                          props.setSelectedTarget({
                            ...props.selectedTarget,
                            molding: e.target.value,
                          });
                        })
                        .catch((err) => {
                          if (err === 403)
                            enqueueSnackbar(t("toasts.unauthorizedError"), {
                              variant: "error",
                            });
                          else
                            enqueueSnackbar(t("toasts.updateMyDesignMolding"), {
                              variant: "error",
                            });
                        });
                    }}
                    label={t("myExpariences.form.molding")}
                  >
                    <MenuItem value="design">
                      {t("myExpariences.form.design")}
                    </MenuItem>
                    <MenuItem value="oak">
                      {t("myExpariences.form.oak")}
                    </MenuItem>
                    <MenuItem value="wood">
                      {t("myExpariences.form.ipe")}
                    </MenuItem>
                    <MenuItem value="white">
                      {t("myExpariences.form.white")}
                    </MenuItem>
                    <MenuItem value="versailles">
                      {t("myExpariences.form.versailles")}
                    </MenuItem>
                    <MenuItem value="versaillesblanc">
                      {t("myExpariences.form.versailleswhite")}
                    </MenuItem>
                  </Select>
                </FormControl>

                <div className={classes.updateContainer}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => updateExparience()}
                  >
                    {props.selectedTarget?.idExparience
                      ? t("myDesign.webVR.updateExparience")
                      : t("myDesign.webVR.associateExparience")}
                  </Button>
                  {props.selectedTarget?.idExparience && (
                    <IconButton
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        history(
                          `/myExpariences/${props.selectedTarget.idExparience}`,
                          {
                            state: { index: 2 },
                          }
                        )
                      }
                      className={classes.actionButton}
                      size="large"
                    >
                      <SettingsIcon className={classes.ADSIconButton} />
                    </IconButton>
                  )}
                </div>
                <div>
                  <div className={classes.qrPosition}>
                    {state.qrUrl && picto && (
                      <CopyToClipboard
                        text={state.qrUrl}
                        onCopy={() => {
                          setCopied(true);
                          enqueueSnackbar(t("toasts.copiedInClipboard"), {
                            variant: "info",
                          });
                        }}
                      >
                        <div ref={canvasRef}>
                          <QRCode
                            id="react-qrcode-logo"
                            // value={props.selectedTarget.arLink || "app.fanartzone.com"}
                            value={state.qrUrl}
                            size={250}
                            ecLevel="H"
                            bgColor="#000000"
                            fgColor="#D3AF5F" //golden
                            includeMargin={true}
                            qrStyle="dots"
                            logoImage={picto}
                            logoHeight={65}
                            logoWidth={65}
                            eyeRadius={[
                              [0, 100, 100, 100],
                              [100, 0, 100, 100],
                              [100, 100, 100, 0],
                            ]}
                            logoOpacity={1} // the higher the opacity is, the lower the logo has to be
                          />
                        </div>
                      </CopyToClipboard>
                    )}
                  </div>
                  {state.qrUrl && picto && (
                    <div>
                      <div className={classes.buttonsQrCode}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={downloadQR}
                          className={classes.downloadQRButton}
                        >
                          {t("myDesign.webVR.downloadQR")}
                        </Button>
                        <a
                          href={state.qrUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconButton
                            className={classes.shareIconButton}
                            size="large"
                          >
                            <LinkIcon />
                          </IconButton>
                        </a>
                        <IconButton
                          className={classes.shareIconButton}
                          onClick={() => shareMedia()}
                          size="large"
                        >
                          <ShareIcon />
                        </IconButton>
                      </div>
                      <div className={classes.copyQrUrlButton}>
                        <CopyToClipboard
                          text={state.qrUrl}
                          onCopy={() => {
                            setCopied(true);
                            enqueueSnackbar(t("toasts.copiedInClipboard"), {
                              variant: "info",
                            });
                          }}
                        >
                          <Button variant="contained" color="primary">
                            {t("myDesign.webVR.copyShortLink")}
                          </Button>
                        </CopyToClipboard>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </StyledContainer>
    );
  }
};

export default WebVR;
